<template>
  <form class="form" @submit.prevent="submit">
    <div class="grid">
      <label class="field">
        <span class="lbl">Name</span>
        <input v-model.trim="name" type="text" placeholder="Coin name" />
      </label>

      <label class="field">
        <span class="lbl">Symbol</span>
        <input v-model.trim="symbol" type="text" placeholder="SYMB" />
      </label>
    </div>

    <label class="field">
      <span class="lbl">Description</span>
      <textarea v-model="description" rows="3" placeholder="Optional"></textarea>
    </label>

    <div class="grid">
      <label class="field">
        <span class="lbl">Website</span>
        <input v-model.trim="website" type="text" placeholder="example.com" />
      </label>

      <label class="field">
        <span class="lbl">Twitter</span>
        <input v-model.trim="twitter" type="text" placeholder="@handle or url" />
      </label>
    </div>

    <label class="field">
      <span class="lbl">Image URL</span>
      <input v-model.trim="image" type="text" placeholder="https://..." />
    </label>

    <label class="field">
      <span class="lbl">Dev wallet</span>
      <select v-model="owner">
        <option value="" disabled>Select wallet…</option>
        <option
          v-for="w in wallets"
          :key="w.public_key"
          :value="w.public_key"
        >
          {{ w.name || 'Unnamed' }} — {{ shortKey(w.public_key) }}
        </option>
      </select>
    </label>

    <div v-if="mode === 'copy' && sourceMint" class="hint">
      Source mint: <span class="mono">{{ shortKey(sourceMint) }}</span>
    </div>

    <div v-if="error" class="error">{{ error }}</div>

    <div class="actions">
      <button class="primary" type="submit" :disabled="busy || !canSubmit">
        {{ busy ? 'Saving…' : (mode === 'copy' ? 'Copy' : 'Create') }}
      </button>

      <button class="ghost" type="button" :disabled="busy" @click="$emit('cancel')">
        Cancel
      </button>
    </div>
  </form>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { shortKey } from '@/components/_shared/format'

const props = defineProps({
  mode: { type: String, default: 'create' }, // create | copy
  initial: { type: Object, default: null },
  wallets: { type: Array, default: () => [] },
  busy: { type: Boolean, default: false },
  error: { type: String, default: '' },
})

const emit = defineEmits(['submit', 'cancel'])

const name = ref('')
const symbol = ref('')
const description = ref('')
const website = ref('')
const twitter = ref('')
const image = ref('')
const owner = ref('')

const sourceMint = computed(() => String(props.initial?.mint || '').trim())

watch(
  () => props.initial,
  (v) => {
    name.value = String(v?.name || '').trim()
    symbol.value = String(v?.symbol || '').trim()
    description.value = String(v?.description || '').trim()
    website.value = String(v?.website || '').trim()
    twitter.value = String(v?.twitter || '').trim()
    image.value = String(v?.image || '').trim()

    // owner НЕ подтягиваю из initial — это осознанный выбор, чтоб не улететь не тем кошельком
    owner.value = ''
  },
  { immediate: true }
)

const canSubmit = computed(() => {
  if (!owner.value) return false
  if (!name.value.trim()) return false
  if (!symbol.value.trim()) return false
  return true
})

function submit() {
  if (!canSubmit.value) return

  emit('submit', {
    mode: props.mode,
    name: name.value.trim(),
    symbol: symbol.value.trim(),
    description: description.value || '',
    website: website.value || '',
    twitter: twitter.value || '',
    image: image.value || '',
    owner: owner.value,
    sourceMint: props.mode === 'copy' ? sourceMint.value : undefined,
  })
}
</script>

<style scoped>
.form{
  display:flex;
  flex-direction:column;
  gap:10px;
}

.grid{
  display:grid;
  grid-template-columns: 1fr 1fr;
  gap:10px;
}

.field{
  display:flex;
  flex-direction:column;
  gap:6px;
}

.lbl{
  font-size:12px;
  color:#9ca3af;
}

input, textarea, select{
  width:100%;
  padding:8px 10px;
  border-radius:8px;
  border:1px solid #333;
  background:#050505;
  color:#e5e7eb;
  font-size:12px;
  outline:none;
}

input:focus, textarea:focus, select:focus{
  border-color:#667eea;
  box-shadow:0 0 0 1px rgba(102,126,234,0.35);
}

.hint{
  font-size:12px;
  color:#9ca3af;
}

.mono{
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.error{
  padding:8px 10px;
  border-radius:8px;
  border:1px solid #4a1f1f;
  background:#2a1111;
  color:#ffaaaa;
  font-size:12px;
}

.actions{
  display:flex;
  justify-content:flex-end;
  gap:8px;
  margin-top:2px;
}

.primary,.ghost{
  padding:6px 12px;
  border-radius:8px;
  border:1px solid #333;
  font-size:12px;
  cursor:pointer;
}

.primary{
  background:#2563eb;
  border-color:#2563eb;
  color:#fff;
}
.primary:disabled{
  opacity:.5;
  cursor:default;
}

.ghost{
  background:transparent;
  color:#aaa;
}
.ghost:hover:enabled{
  background:#1a1a1a;
}

@media (max-width: 560px){
  .grid{ grid-template-columns: 1fr; }
}
</style>
